import React from 'react';
import { ArrowRight } from 'lucide-react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  loading?: boolean;
}
/**
 * Accessible Button component with consistent styling and keyboard support
 * Supports both button and link behavior with proper ARIA attributes
 *
 * @param variant - Button style variant (default: 'primary')
 * @param size - Button size (default: 'md')
 * @param href - If provided, renders as a link
 * @param icon - Optional icon element
 * @param iconPosition - Position of icon (default: 'right')
 * @param loading - Show loading state
 *
 * @example
 * <Button variant="primary" icon={<ArrowRight />}>
 *   Get Started
 * </Button>
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  icon,
  iconPosition = 'right',
  fullWidth = false,
  loading = false,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center gap-3 font-semibold rounded-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#3ab795]/50 focus:ring-offset-2 focus:ring-offset-[#0a0a0a] disabled:opacity-50 disabled:cursor-not-allowed';
  const variantStyles = {
    primary: 'bg-[#3ab795] text-white hover:bg-[#2d9f7e] active:scale-95 shadow-lg hover:shadow-[0_8px_24px_rgba(58,183,149,0.3)]',
    secondary: 'bg-white text-[#1a1a1a] hover:bg-gray-100 active:scale-95 shadow-lg',
    outline: 'bg-transparent text-white border-2 border-white/10 hover:border-[#3ab795] hover:bg-[#3ab795]/10 active:scale-95'
  };
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  const widthClass = fullWidth ? 'w-full' : 'w-fit';
  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthClass} ${className}`;
  const content = <>
      {loading && <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" data-id="element-2156">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2157" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2158" />
        </svg>}
      {!loading && icon && iconPosition === 'left' && icon}
      <span data-id="element-2159">{children}</span>
      {!loading && icon && iconPosition === 'right' && icon}
    </>;
  if (href) {
    return <a href={href} className={combinedClassName} aria-disabled={disabled || loading} {...props as React.AnchorHTMLAttributes<HTMLAnchorElement>} data-id="element-2160">
        {content}
      </a>;
  }
  return <button className={combinedClassName} disabled={disabled || loading} aria-busy={loading} {...props} data-id="element-2161">
      {content}
    </button>;
}